import { slerpInto, writeTrs, multiplyMatrices, copyMatrix, multiplyQuaternion, inverseQuaternion, eulerQuaternion, betweenDirections, type Quaternion } from "./character-pose.js";
import { WaterLocomotion } from "@endclock/shared/player-locomotion";
import { PlayerGait, PlayerMotionMode, PlayerStance, PLAYER_COMMAND_SECONDS, clampPlayerPitch, playerYawDifference } from "@endclock/shared/player-control";
import { ArticulatedRagdoll, type RagdollGround } from "./articulated-ragdoll.js";
import { FLASHLIGHT_SOCKET } from "./held-equipment.js";
import type {
  CharacterAnimationClip,
  CharacterAnimationTrack,
  CharacterAsset,
  CharacterClipName,
} from "./character-asset.js";

export const CHARACTER_HEIGHT_METERS = 1.78;
export const CHARACTER_IDLE_ENTER_SPEED = 0.1;
export const CHARACTER_IDLE_EXIT_SPEED = 0.18;
export const CHARACTER_RUN_MINIMUM_SPEED = 3.35;
export const CHARACTER_TURN_SPEED = 12;
export const CHARACTER_BLEND_SECONDS = 0.3;
export const CHARACTER_WALK_CYCLE_DISTANCE = 1.85;
export const CHARACTER_RUN_CYCLE_DISTANCE = 2.482;

// The authored boots have six sole corners each, rigidly weighted to the shin.
// Extract them once per shared asset; frame updates only transform these points.
const soleContactCache = new WeakMap<CharacterAsset, Float32Array>();
function soleContactPoints(asset: CharacterAsset): Float32Array {
  const cached = soleContactCache.get(asset);
  if (cached) return cached;
  const points: number[] = [], seen = new Set<string>();
  const { positions, joints, weights } = asset.geometry;
  for (let vertex = 0; vertex < positions.length / 3; vertex++) {
    const p = vertex * 3, skin = vertex * 4, joint = joints[skin]!;
    if (weights[skin]! < .999 || !asset.joints[joint]!.name.startsWith("lower_leg_")
      || positions[p + 1]! > asset.groundOriginY + .013) continue;
    const key = `${joint}:${positions[p]}:${positions[p + 1]}:${positions[p + 2]}`;
    if (seen.has(key)) continue;
    seen.add(key); points.push(joint * 16, positions[p]!, positions[p + 1]!, positions[p + 2]!);
  }
  const result = new Float32Array(points);
  soleContactCache.set(asset, result);
  return result;
}

export function characterPixelHeight(
  zoom: number,
  viewportHeight = 390,
  pitchRadians = (48 * Math.PI) / 180,
  height = CHARACTER_HEIGHT_METERS,
): number {
  return (height * Math.cos(pitchRadians) * viewportHeight) / zoom;
}

export function selectAnimationState(
  speed: number,
  running: boolean,
  previous: CharacterClipName,
  locomotion: WaterLocomotion = WaterLocomotion.Ground,
): CharacterClipName {
  // Stationary swimmers keep stroking to support themselves at the surface.
  if (locomotion === WaterLocomotion.Swimming) return "SWIM";
  const threshold = previous === "IDLE" ? CHARACTER_IDLE_EXIT_SPEED : CHARACTER_IDLE_ENTER_SPEED;
  if (!Number.isFinite(speed) || speed < threshold) return "IDLE";
  return locomotion === WaterLocomotion.Ground && running && speed >= CHARACTER_RUN_MINIMUM_SPEED ? "RUN" : "WALK";
}

export function facingAngle(velocityX: number, velocityZ: number, fallback: number): number {
  return Math.hypot(velocityX, velocityZ) >= CHARACTER_IDLE_ENTER_SPEED
    ? Math.atan2(velocityX, velocityZ)
    : fallback;
}

export function smoothFacing(current: number, target: number, delta: number, speed = CHARACTER_TURN_SPEED): number {
  const difference = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  return current + difference * (1 - Math.exp(-speed * Math.max(0, delta)));
}

export function animationPlaybackRate(state: CharacterClipName, speed: number, clipDuration: number): number {
  if (state === "IDLE" || state === "CROUCH_IDLE" || state.startsWith("GET_UP") || state.startsWith("TURN")) return 1;
  const finiteSpeed = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  if (state === "SWIM") return Math.max(0.65, Math.min(1.25, finiteSpeed / 2.6));
  const cycleDistance = state === "RUN" ? CHARACTER_RUN_CYCLE_DISTANCE : state === "SPRINT" ? 3.0 : state === "CROUCH_WALK" ? 1.15 : CHARACTER_WALK_CYCLE_DISTANCE;
  return Math.max(0.18, finiteSpeed / cycleDistance) * clipDuration;
}

export function boundedCharacterWetness(wetness: number): number {
  return Number.isFinite(wetness) ? Math.max(0, Math.min(1, wetness)) : 0;
}

export interface CharacterPoseControl {
  readonly bodyYaw: number;
  readonly aimYaw: number;
  readonly aimPitch: number;
  readonly stance: PlayerStance;
  readonly gait: PlayerGait;
  readonly motionMode: PlayerMotionMode;
  readonly velocityX?: number;
  readonly velocityZ?: number;
  readonly velocityY?: number;
  readonly ragdollEvent?: number;
  readonly ragdollStartTick?: number;
  readonly motionStartTick?: number;
  readonly impulseX?: number;
  readonly impulseZ?: number;
  readonly getUpVariant?: number;
  readonly motionEpoch?: number;
  readonly stateAgeSeconds?: number;
}

export class CharacterAnimator {
  state: CharacterClipName = "IDLE";
  time = 0;
  heldItemPose = false;
  ragdoll: ArticulatedRagdoll | undefined;
  recoveryFromCapturedPose = false;
  private heldPoseAmount = 0;
  private motionMode = PlayerMotionMode.Active;
  private ragdollEvent = -1;
  private ragdollEpoch = -1;
  private ragdollStartTick: number | undefined;
  private ragdollStateAge: number | undefined;
  private ragdollUnclockedElapsed = 0;
  private recoveryStateAge: number | undefined;
  private recoveryEvent = -1;
  private recoveryEpoch = -1;
  private recoveryStartTick: number | undefined;
  private previousBodyYaw: number | undefined;
  private turnAmount = 0;
  readonly palette: Float32Array;
  readonly runtimeBytes: number;
  private blendElapsed = CHARACTER_BLEND_SECONDS;
  private readonly translations: Float32Array;
  private readonly rotations: Float32Array;
  private readonly scales: Float32Array;
  private readonly blendTranslations: Float32Array;
  private readonly blendRotations: Float32Array;
  private readonly blendScales: Float32Array;
  private readonly directionTranslations: Float32Array;
  private readonly directionRotations: Float32Array;
  private readonly directionScales: Float32Array;
  private readonly localMatrices: Float32Array;
  private readonly globalMatrices: Float32Array;
  private readonly solePoints: Float32Array;

  constructor(private readonly asset: CharacterAsset) {
    const count = asset.joints.length;
    this.solePoints = soleContactPoints(asset);
    this.translations = new Float32Array(count * 3);
    this.rotations = new Float32Array(count * 4);
    this.scales = new Float32Array(count * 3);
    this.blendTranslations = new Float32Array(count * 3);
    this.blendRotations = new Float32Array(count * 4);
    this.blendScales = new Float32Array(count * 3);
    this.directionTranslations = new Float32Array(count * 3);
    this.directionRotations = new Float32Array(count * 4);
    this.directionScales = new Float32Array(count * 3);
    this.localMatrices = new Float32Array(count * 16);
    this.globalMatrices = new Float32Array(count * 16);
    this.palette = new Float32Array(count * 16);
    this.runtimeBytes = [this.translations, this.rotations, this.scales, this.blendTranslations,
      this.blendRotations, this.blendScales, this.directionTranslations, this.directionRotations,
      this.directionScales, this.localMatrices, this.globalMatrices, this.palette].reduce((sum, array) => sum + array.byteLength, 0);
    this.resetPose(); this.captureBlend(); this.updatePalette();
  }

  /** Includes the single remaining physics update when recovery is first observed. */
  get hasActiveRagdoll(): boolean { return this.motionMode === PlayerMotionMode.Ragdoll && this.ragdoll !== undefined; }

  update(speed: number, running: boolean, delta: number, locomotion: WaterLocomotion = WaterLocomotion.Ground,
    control?: CharacterPoseControl, ground?: RagdollGround, allowRagdoll = true): void {
    const safeDelta = Number.isFinite(delta) ? Math.max(0, delta) : 0;
    const mode = control?.motionMode ?? PlayerMotionMode.Active;
    const priorMode = this.motionMode;
    this.motionMode = mode;
    const recoveryChanged = mode === PlayerMotionMode.GettingUp && (priorMode !== mode
      || this.recoveryEvent !== (control?.ragdollEvent ?? 0) || this.recoveryEpoch !== (control?.motionEpoch ?? 0)
      || this.recoveryStartTick !== (control?.motionStartTick ?? control?.ragdollStartTick));
    if (mode !== PlayerMotionMode.GettingUp || recoveryChanged) this.recoveryStateAge = undefined;
    if (recoveryChanged) {
      this.recoveryEvent = control?.ragdollEvent ?? 0; this.recoveryEpoch = control?.motionEpoch ?? 0;
      this.recoveryStartTick = control?.motionStartTick ?? control?.ragdollStartTick;
      // A whole intervening fall may have occurred while this actor was culled.
      if (priorMode === PlayerMotionMode.GettingUp) this.ragdoll = undefined;
    }
    if (mode === PlayerMotionMode.Ragdoll && allowRagdoll) {
      const event = control?.ragdollEvent ?? 0, epoch = control?.motionEpoch ?? 0;
      const startTick = control?.motionStartTick ?? control?.ragdollStartTick;
      if (!this.ragdoll || this.ragdollEvent !== event || this.ragdollEpoch !== epoch || this.ragdollStartTick !== startTick) {
        this.ragdollEvent = event; this.ragdollEpoch = epoch; this.ragdollStartTick = startTick;
        this.ragdollStateAge = undefined;
        this.ragdollUnclockedElapsed = 0;
        this.ragdoll = new ArticulatedRagdoll(this.asset, this.translations, this.rotations,
          control?.impulseX ?? 0, control?.impulseZ ?? -2, control?.getUpVariant ?? 0);
      }
      let elapsed = safeDelta;
      const stateAge = control?.stateAgeSeconds;
      if (stateAge !== undefined && Number.isFinite(stateAge)) {
        const age = Math.max(0, stateAge);
        // A frame can begin before the impact. Existing solvers instead follow
        // monotonic event age, including time spent outside the visibility set.
        elapsed = this.ragdollStateAge === undefined
          ? Math.min(safeDelta, Math.max(0, age - this.ragdollUnclockedElapsed))
          : Math.max(0, age - this.ragdollStateAge);
        this.ragdollStateAge = Math.max(this.ragdollStateAge ?? this.ragdollUnclockedElapsed, age);
        this.ragdollUnclockedElapsed = 0;
      } else {
        // A later valid age must not replay time already charged by this fallback.
        if (this.ragdollStateAge === undefined) this.ragdollUnclockedElapsed += safeDelta;
        else this.ragdollStateAge += safeDelta;
      }
      // The age cursor advances even when the solver drops time above its cap;
      // old debt never causes repeated catch-up work on later render frames.
      this.ragdoll.update(elapsed, ground);
      this.translations.set(this.ragdoll.translations); this.rotations.set(this.ragdoll.rotations);
      this.updatePalette(); return;
    }
    if (mode === PlayerMotionMode.Ragdoll && !allowRagdoll) this.ragdoll = undefined;
    if (priorMode === PlayerMotionMode.Ragdoll && mode === PlayerMotionMode.GettingUp) {
      const sameEvent = this.ragdollEvent === (control?.ragdollEvent ?? 0) && this.ragdollEpoch === (control?.motionEpoch ?? 0);
      if (this.ragdoll && sameEvent && allowRagdoll) {
        const recoveryTick = control?.motionStartTick ?? control?.ragdollStartTick;
        const previousTick = this.ragdollStartTick;
        const chargedAge = this.ragdollStateAge ?? this.ragdollUnclockedElapsed;
        let remaining = 0;
        const frameRemainder = control?.stateAgeSeconds !== undefined && Number.isFinite(control.stateAgeSeconds)
          ? Math.max(0, safeDelta - Math.max(0, control.stateAgeSeconds)) : 0;
        if (previousTick !== undefined && recoveryTick !== undefined) {
          if (Number.isFinite(previousTick) && Number.isFinite(recoveryTick) && recoveryTick >= previousTick) {
            // An unclocked late join has no event-age cursor: its small tally
            // cannot justify simulating the unseen part of the entire event.
            remaining = this.ragdollStateAge !== undefined
              ? Math.max(0, (recoveryTick - previousTick) * PLAYER_COMMAND_SECONDS - chargedAge)
              : recoveryTick > previousTick ? frameRemainder : 0;
          }
        } else {
          // Without authoritative boundary ticks only the part of this visible
          // frame before the known recovery age is attributable to the fall.
          remaining = frameRemainder;
        }
        this.ragdoll.update(remaining, ground);
        this.ragdollStateAge = chargedAge + remaining; this.ragdollUnclockedElapsed = 0;
        this.translations.set(this.ragdoll.translations); this.rotations.set(this.ragdoll.rotations);
      }
      if (!sameEvent) this.ragdoll = undefined;
      this.recoveryFromCapturedPose = true;
    }
    let selected = selectAnimationState(speed, running, this.state, locomotion);
    if (control && locomotion !== WaterLocomotion.Swimming) {
      const bodyDelta = this.previousBodyYaw === undefined ? 0 : playerYawDifference(this.previousBodyYaw, control.bodyYaw);
      const angularSpeed = safeDelta > 0 ? bodyDelta / safeDelta : 0;
      this.turnAmount += (angularSpeed - this.turnAmount) * (1 - Math.exp(-12 * safeDelta));
      this.previousBodyYaw = control.bodyYaw;
      if (control.stance === PlayerStance.Crouched) selected = speed > CHARACTER_IDLE_EXIT_SPEED ? "CROUCH_WALK" : "CROUCH_IDLE";
      else if (speed > CHARACTER_IDLE_EXIT_SPEED) selected = control.gait === PlayerGait.Sprint ? "SPRINT" : control.gait === PlayerGait.Run ? "RUN" : "WALK";
      else if (Math.abs(this.turnAmount) > 0.06) selected = this.turnAmount < 0 ? "TURN_LEFT" : "TURN_RIGHT";
    }
    if (mode !== PlayerMotionMode.Active) selected = control?.getUpVariant === 1 ? "GET_UP_BACK" : "GET_UP_FRONT";
    const nextState = this.asset.clips[selected] ? selected : "IDLE";
    if (nextState !== this.state || priorMode !== mode || recoveryChanged) {
      const phase = this.time / this.asset.clips[this.state]!.duration % 1;
      const keepPhase = speed > 0.18 && priorMode === PlayerMotionMode.Active && mode === PlayerMotionMode.Active
        && this.state !== "SWIM" && nextState !== "SWIM" && this.state !== "IDLE";
      this.captureBlend(); this.state = nextState;
      this.time = keepPhase ? phase * this.asset.clips[nextState]!.duration : 0; this.blendElapsed = 0;
    }
    const clip = this.asset.clips[this.state]!;
    const recovering = mode === PlayerMotionMode.GettingUp;
    let playback = animationPlaybackRate(this.state, speed, clip.duration);
    if (control && mode === PlayerMotionMode.Active && locomotion === WaterLocomotion.Ground && speed > 0.18 && control.stance === PlayerStance.Standing) {
      const vx = control.velocityX ?? Math.sin(control.bodyYaw) * speed, vz = control.velocityZ ?? Math.cos(control.bodyYaw) * speed;
      const side = Math.abs(vx * Math.cos(control.bodyYaw) - vz * Math.sin(control.bodyYaw));
      const forward = vx * Math.sin(control.bodyYaw) + vz * Math.cos(control.bodyYaw);
      const baseDistance = this.state === "SPRINT" ? 3 : this.state === "RUN" ? CHARACTER_RUN_CYCLE_DISTANCE : CHARACTER_WALK_CYCLE_DISTANCE;
      const weightedDistance = (side * 1.6 + Math.abs(forward) * (forward < 0 ? 1.45 : baseDistance)) / (side + Math.abs(forward) || 1);
      playback = speed / weightedDistance * clip.duration;
    }
    let blendDelta = safeDelta;
    if (recovering && control?.stateAgeSeconds !== undefined && Number.isFinite(control.stateAgeSeconds)) {
      const previousAge = this.recoveryStateAge ?? 0;
      this.recoveryStateAge = Math.max(previousAge, control.stateAgeSeconds, 0);
      blendDelta = this.recoveryStateAge - previousAge;
      this.time = this.recoveryStateAge;
    } else {
      this.time += safeDelta * (recovering ? 1 : playback);
      if (recovering) this.recoveryStateAge = (this.recoveryStateAge ?? 0) + safeDelta;
    }
    this.resetPose();
    this.sampleClip(clip, mode === PlayerMotionMode.Ragdoll ? 0 : this.time, mode !== PlayerMotionMode.Active);
    if (control && mode === PlayerMotionMode.Active && locomotion !== WaterLocomotion.Swimming && speed > 0.18) {
      this.applyDirectionalLocomotion(control, speed, clip);
    }
    if (mode === PlayerMotionMode.Active && locomotion !== WaterLocomotion.Swimming) this.applyAim(control, safeDelta);
    if (this.blendElapsed < CHARACTER_BLEND_SECONDS) {
      this.blendElapsed = Math.min(CHARACTER_BLEND_SECONDS, this.blendElapsed + blendDelta);
      blendPose(this.translations, this.rotations, this.scales, this.blendTranslations,
        this.blendRotations, this.blendScales, smoothstep(this.blendElapsed / CHARACTER_BLEND_SECONDS));
    }
    if (mode === PlayerMotionMode.Active) this.ragdoll = undefined;
    this.updatePalette();
    if (control && mode === PlayerMotionMode.Active && locomotion === WaterLocomotion.Ground
      && (control.stance === PlayerStance.Crouched || control.gait === PlayerGait.Walk)
      && Math.abs(ground?.heightAt(0, 0) ?? 0) <= .25) this.groundOrdinaryPose(ground);
  }

  /** Visual pelvis only: retain authored joints and the authoritative root. */
  private groundOrdinaryPose(ground?: RagdollGround): void {
    let clearance = Infinity;
    for (let point = 0; point < this.solePoints.length; point += 4) {
      const m = this.solePoints[point]!, x = this.solePoints[point + 1]!, y = this.solePoints[point + 2]!, z = this.solePoints[point + 3]!;
      const px = this.palette[m]! * x + this.palette[m + 4]! * y + this.palette[m + 8]! * z + this.palette[m + 12]!;
      const py = this.palette[m + 1]! * x + this.palette[m + 5]! * y + this.palette[m + 9]! * z + this.palette[m + 13]!;
      const pz = this.palette[m + 2]! * x + this.palette[m + 6]! * y + this.palette[m + 10]! * z + this.palette[m + 14]!;
      clearance = Math.min(clearance, py - (ground?.heightAt(px, pz) ?? 0));
    }
    if (!Number.isFinite(clearance) || Math.abs(clearance) < 1e-7) return;
    // A small correction fixes shortened hip reach without pulling a character
    // far up/down a cliff. It does not lock either foot horizontally or add IK.
    this.translations[4]! += Math.max(-.18, Math.min(.18, -clearance));
    this.updatePalette();
  }

  /** Snapshot allocation is for diagnostics/tests, never the frame hot path. */
  capturePose(): { translations: Float32Array; rotations: Float32Array } {
    return { translations: new Float32Array(this.translations), rotations: new Float32Array(this.rotations) };
  }

  /** Aim correction uses the actual lens position and rotates the same forearm as its mesh. */
  aimHeldEmitterAt(point: readonly [number, number, number]): void {
    if (!this.heldItemPose || this.motionMode !== PlayerMotionMode.Active || this.state === "SWIM" || !point.every(Number.isFinite)) return;
    const joint = this.asset.joints.findIndex((entry) => entry.name === FLASHLIGHT_SOCKET.joint);
    if (joint < 0) return;
    for (let pass = 0; pass < 3; pass += 1) {
      const m = joint * 16, bind = FLASHLIGHT_SOCKET.position;
      const origin = [0, 1, 2].map((row) => this.palette[m + row]! * bind[0] + this.palette[m + row + 4]! * bind[1]
        + this.palette[m + row + 8]! * bind[2] + this.palette[m + row + 12]!);
      const target = [point[0] - origin[0]!, point[1] - origin[1]!, point[2] - origin[2]!];
      if (Math.hypot(...target) < 0.2) return;
      const direction = [-this.globalMatrices[m + 4]!, -this.globalMatrices[m + 5]!, -this.globalMatrices[m + 6]!];
      const global = multiplyQuaternion(betweenDirections(direction, target), this.globalRotation(joint));
      const local = multiplyQuaternion(inverseQuaternion(this.globalRotation(this.asset.joints[joint]!.parent)), global);
      this.rotations.set(local, joint * 4); this.updatePalette();
    }
  }

  private captureBlend(): void {
    this.blendTranslations.set(this.translations); this.blendRotations.set(this.rotations); this.blendScales.set(this.scales);
  }

  private applyDirectionalLocomotion(control: CharacterPoseControl, speed: number, clip: CharacterAnimationClip): void {
    const vx = control.velocityX ?? Math.sin(control.bodyYaw) * speed, vz = control.velocityZ ?? Math.cos(control.bodyYaw) * speed;
    const right = (vx * Math.cos(control.bodyYaw) - vz * Math.sin(control.bodyYaw)) / speed;
    const forward = (vx * Math.sin(control.bodyYaw) + vz * Math.cos(control.bodyYaw)) / speed;
    const lateral = Math.min(1, Math.abs(right)), backward = Math.max(0, -forward);
    if (control.stance === PlayerStance.Crouched) {
      // Crouch preserves hip/knee flexion while rotating the stepping component
      // into movement space. This keeps the crouched centre of mass unchanged.
      for (const index of [4, 6]) {
        const q = index * 4, phase = this.time / clip.duration * Math.PI * 2;
        const swing = Math.cos(phase) * (index === 4 ? -1 : 1) * 0.23;
        this.rotations.set(eulerQuaternion(-0.76 - swing * forward, 0, swing * right), q);
      }
      return;
    }
    const phase = this.time / clip.duration;
    const directionalBlend = (name: CharacterClipName, amount: number) => {
      const directional = this.asset.clips[name]; if (!directional || amount <= 0) return;
      this.directionTranslations.set(this.translations); this.directionRotations.set(this.rotations); this.directionScales.set(this.scales);
      this.sampleClip(directional, phase * directional.duration);
      blendPose(this.translations, this.rotations, this.scales, this.directionTranslations,
        this.directionRotations, this.directionScales, amount);
    };
    const longitudinal = Math.abs(forward);
    directionalBlend("WALK_BACK", backward / (longitudinal || 1));
    directionalBlend(right < 0 ? "STRAFE_LEFT" : "STRAFE_RIGHT", lateral / (longitudinal + lateral || 1));
  }

  private applyAim(control: CharacterPoseControl | undefined, delta: number): void {
    const pitch = clampPlayerPitch(control?.aimPitch ?? 0);
    const yaw = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, playerYawDifference(control?.bodyYaw ?? 0, control?.aimYaw ?? 0)));
    if (control) {
      for (const [joint, pitchWeight, yawWeight] of [[2, 0.30, 0.62], [3, 0.20, 0.38], [8, 0.12, 0.1]] as const) {
        const offset = joint * 4;
        const base = Array.from(this.rotations.slice(offset, offset + 4)) as unknown as Quaternion;
        this.rotations.set(multiplyQuaternion(base, eulerQuaternion(-pitch * pitchWeight, yaw * yawWeight)), offset);
      }
    }
    this.heldPoseAmount += ((this.heldItemPose ? 1 : 0) - this.heldPoseAmount) * (1 - Math.exp(-12 * delta));
    if (this.heldPoseAmount < 0.001) return;
    const elbow = control?.gait === PlayerGait.Sprint ? 1.17 : 1.0;
    const desired = multiplyQuaternion(eulerQuaternion(0, yaw), eulerQuaternion(-Math.PI / 2 - pitch + elbow));
    const upper = multiplyQuaternion(inverseQuaternion(this.globalRotation(2)), desired);
    for (const [joint, target] of [[10, upper], [11, eulerQuaternion(-elbow)]] as const) {
      const offset = joint * 4;
      this.directionRotations.set(target, offset);
      slerpInto(this.rotations, offset, this.rotations, offset, this.directionRotations, offset, this.heldPoseAmount);
    }
  }

  private globalRotation(joint: number): Quaternion {
    const offset = joint * 4;
    const local: Quaternion = [this.rotations[offset]!, this.rotations[offset + 1]!, this.rotations[offset + 2]!, this.rotations[offset + 3]!];
    const parent = this.asset.joints[joint]!.parent;
    return parent < 0 ? local : multiplyQuaternion(this.globalRotation(parent), local);
  }

  private resetPose(): void {
    for (let index = 0; index < this.asset.joints.length; index += 1) {
      const joint = this.asset.joints[index]!;
      const translationOffset = index * 3;
      const rotationOffset = index * 4;
      this.translations[translationOffset] = joint.translation[0];
      this.translations[translationOffset + 1] = joint.translation[1];
      this.translations[translationOffset + 2] = joint.translation[2];
      this.rotations[rotationOffset] = joint.rotation[0];
      this.rotations[rotationOffset + 1] = joint.rotation[1];
      this.rotations[rotationOffset + 2] = joint.rotation[2];
      this.rotations[rotationOffset + 3] = joint.rotation[3];
      this.scales[translationOffset] = joint.scale[0];
      this.scales[translationOffset + 1] = joint.scale[1];
      this.scales[translationOffset + 2] = joint.scale[2];
    }
  }

  private sampleClip(clip: CharacterAnimationClip, time: number, once = false): void {
    const localTime = clip.duration > 0 ? once ? Math.min(clip.duration, time) : time % clip.duration : 0;
    for (let index = 0; index < clip.tracks.length; index += 1) this.sampleTrack(clip.tracks[index]!, localTime);
  }

  private sampleTrack(track: CharacterAnimationTrack, time: number): void {
    const times = track.times;
    let upper = 1;
    while (upper < times.length && times[upper]! <= time) upper += 1;
    const first = upper >= times.length ? times.length - 1 : Math.max(0, upper - 1);
    const second = upper >= times.length ? 0 : upper;
    const firstTime = times[first]!;
    const secondTime = upper >= times.length ? this.asset.clips[this.state]!.duration + times[0]! : times[second]!;
    const amount = secondTime > firstTime ? (time - firstTime) / (secondTime - firstTime) : 0;
    const width = track.path === "rotation" ? 4 : 3;
    const target = track.path === "translation" ? this.translations : track.path === "rotation" ? this.rotations : this.scales;
    const targetOffset = track.joint * width;
    const firstOffset = first * width;
    const secondOffset = second * width;
    if (track.path === "rotation") {
      slerpInto(target, targetOffset, track.values, firstOffset, track.values, secondOffset, amount);
    } else {
      for (let component = 0; component < width; component += 1) {
        const a = track.values[firstOffset + component]!;
        target[targetOffset + component] = a + (track.values[secondOffset + component]! - a) * amount;
      }
    }
  }

  private updatePalette(): void {
    for (let jointIndex = 0; jointIndex < this.asset.joints.length; jointIndex += 1) {
      const translationOffset = jointIndex * 3;
      const rotationOffset = jointIndex * 4;
      const matrixOffset = jointIndex * 16;
      writeTrs(
        this.localMatrices,
        matrixOffset,
        this.translations,
        translationOffset,
        this.rotations,
        rotationOffset,
        this.scales,
        translationOffset,
      );
      const parent = this.asset.joints[jointIndex]!.parent;
      if (parent < 0) {
        copyMatrix(this.globalMatrices, matrixOffset, this.localMatrices, matrixOffset);
      } else {
        multiplyMatrices(this.globalMatrices, matrixOffset, this.globalMatrices, parent * 16, this.localMatrices, matrixOffset);
      }
      multiplyMatrices(
        this.palette,
        matrixOffset,
        this.globalMatrices,
        matrixOffset,
        this.asset.inverseBindMatrices,
        matrixOffset,
      );
    }
  }
}

function blendPose(
  translations: Float32Array,
  rotations: Float32Array,
  scales: Float32Array,
  sourceTranslations: Float32Array,
  sourceRotations: Float32Array,
  sourceScales: Float32Array,
  amount: number,
): void {
  for (let offset = 0; offset < translations.length; offset += 1) {
    translations[offset] = sourceTranslations[offset]! + (translations[offset]! - sourceTranslations[offset]!) * amount;
    scales[offset] = sourceScales[offset]! + (scales[offset]! - sourceScales[offset]!) * amount;
  }
  for (let offset = 0; offset < rotations.length; offset += 4) {
    slerpInto(rotations, offset, sourceRotations, offset, rotations, offset, amount);
  }
}

function smoothstep(value: number): number {
  const clamped = Math.max(0, Math.min(1, value));
  return clamped * clamped * (3 - 2 * clamped);
}
